/**
 * Adds DOM helpers to the public pollHero API.
 *
 * The helpers intentionally do not depend on a project being initialized, so
 * they can also be used by tag-manager code immediately after the widget
 * script has loaded.
 */
export function initializePollHeroHelpers() {
    window.pollHero = window.pollHero || {};

    window.pollHero.waitForElement = function (selector, callback) {
        if (typeof selector !== 'string' || typeof callback !== 'function') return;

        const notify = (element) => callback(element);

        document.querySelectorAll(selector).forEach(notify);

        if (typeof MutationObserver === 'undefined' || !document.documentElement) return;

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType !== 1) return;

                    if (node.matches(selector)) notify(node);
                    node.querySelectorAll(selector).forEach(notify);
                });
            });
        });

        observer.observe(document.documentElement, { childList: true, subtree: true });
    };

    window.pollHero.elementInViewport = function (selector, callback) {
        if (typeof selector !== 'string' || typeof callback !== 'function') return;
        if (typeof IntersectionObserver === 'undefined') return;

        const observedElements = new WeakSet();
        const triggeredElements = new WeakSet();
        const intersectingElements = new WeakSet();
        const visibilityState = new WeakMap();

        const isVisible = (element) => {
            for (let current = element; current; current = current.parentElement) {
                const style = window.getComputedStyle(current);
                if (
                    style.display === 'none' ||
                    style.visibility === 'hidden' ||
                    style.visibility === 'collapse' ||
                    Number.parseFloat(style.opacity) === 0
                ) {
                    return false;
                }
            }

            return true;
        };

        const notifyIfEligible = (element, triggeredByVisibilityChange = false) => {
            const visible = isVisible(element);
            const wasVisible = visibilityState.get(element);
            visibilityState.set(element, visible);

            if (!intersectingElements.has(element) || !visible) return;
            if (triggeredElements.has(element)) return;
            if (triggeredByVisibilityChange && wasVisible === visible) return;

            callback(element);
            triggeredElements.add(element);
            intersectionObserver.unobserve(element);
        };

        const intersectionObserver = new IntersectionObserver(
            (entries, observer) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting || entry.intersectionRatio < 1) {
                        intersectingElements.delete(entry.target);
                        return;
                    }

                    intersectingElements.add(entry.target);
                    notifyIfEligible(entry.target);
                });
            },
            { threshold: 1 },
        );

        const observeElement = (element) => {
            if (!observedElements.has(element)) {
                observedElements.add(element);
                intersectionObserver.observe(element);
            }
        };

        document.querySelectorAll(selector).forEach(observeElement);

        if (typeof MutationObserver !== 'undefined' && document.documentElement) {
            const updateElement = (element) => {
                if (element.nodeType !== 1) return;

                if (element.matches(selector)) {
                    observeElement(element);
                    notifyIfEligible(element, true);
                }
                element.querySelectorAll(selector).forEach((target) => {
                    observeElement(target);
                    notifyIfEligible(target, true);
                });
            };

            const mutationObserver = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes') updateElement(mutation.target);
                    mutation.addedNodes.forEach(updateElement);
                });
            });

            mutationObserver.observe(document.documentElement, {
                childList: true,
                attributes: true,
                attributeFilter: ['class', 'style', 'hidden'],
                subtree: true,
            });

            document.addEventListener('transitionend', (event) => updateElement(event.target));
        }
    };
}
